import React from 'react';
import { Link, Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { MdLogout, MdLightMode, MdDarkMode } from 'react-icons/md';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import HomePage from './HomePage';
import DeltaSection from './delta/DeltaSection';
import PoliciesPage from './delta/PoliciesPage';
import ResearchPage from './research/ResearchPage';
import ChatterPage from './research/ChatterPage';
import ResearchOSHome from './research/ResearchOSHome';
import InvestmentDashboard from './dashboard/InvestmentDashboard';
import AutomatedTrading from './trading/AutomatedTrading';
import CompanyProfile from './research/company/CompanyProfile';
import ErrorBoundary from '../components/ErrorBoundary';
import PolicyDocumentPage from './delta/PolicyDocumentPage';
import AssetNexusPage from './delta/AssetNexusPage';
import InventoryPage from './delta/InventoryPage';

// The application shell.
//
// TWO LEVELS, NOT ONE ROW OF TABS. Signing in lands on HomePage — a short list of areas —
// and each area owns its own navigation. The five peer tabs this replaced put the
// reorganisation plan and the live position register side by side as if they were the same
// kind of thing; they are a plan being approved and a book being measured. The plan's three
// parts now sit inside Project Delta, which is also where the next area can go without
// growing a tab strip past what a phone can show.
//
// There is no sidebar and no module switcher: this deployment holds nothing else, and the
// wordmark is the way back to the top.
//
// A read-only user (Shareholder, Auditor) sees the same screens; the difference is that no
// write control renders and the API refuses the request regardless. The register is hidden
// AND unrouted for them, so a bookmarked /register cannot render it — it falls through to
// the catch-all instead.

// The screens whose figures are INVENTED, so the footer prints the illustrative caveat there.
// Only the AUM Dashboard now — Automated Trading went live on the real Maghaweer feed, and a
// footer still calling it illustrative is a second, stale disclaimer contradicting the live
// data above it. The trading PAGE already shows its own banner only when it genuinely falls
// back to illustrative (no feed), which is the correct, self-adjusting signal; this static
// list is not. Put '/trading' back only if that feed is retired.
const ILLUSTRATIVE_ROUTES = ['/dashboard'];

export default function AppShell() {
  const { logout, canWrite } = useAuth();
  const { isDark, toggleTheme } = useTheme();
  const nav = useNavigate();
  const { pathname } = useLocation();

  const signOut = async () => {
    await logout();
    nav('/login', { replace: true });
  };

  return (
    <div className="min-h-screen bg-[#f7f9fc] dark:bg-[#0b0d12]">
      <header className="border-b border-gray-200 bg-white dark:border-gray-800 dark:bg-[#0d1117]">
        <div className="mx-auto flex max-w-6xl items-center gap-3 px-3 py-3 sm:px-4">
          <Link to="/" className="min-w-0 flex-1">
            <div className="font-serif text-base text-gray-900 dark:text-gray-100 sm:text-lg">
              Maghaweer<span className="text-brand-gold">.ai</span>
            </div>
            {/* No name, role or "read-only" strapline. It was noise on every screen, and
                it told the reader nothing they could act on — what a role permits is
                enforced server-side and is unaffected by whether the header announces it. */}
          </Link>
          <button onClick={toggleTheme}
                  className="shrink-0 rounded p-2 text-gray-400 hover:text-brand-gold"
                  title={isDark ? 'Light theme' : 'Dark theme'}>
            {isDark ? <MdLightMode size={18} /> : <MdDarkMode size={18} />}
          </button>
          <button onClick={signOut}
                  className="flex shrink-0 items-center gap-1.5 rounded px-2 py-1.5 text-[12px] text-gray-500 hover:text-brand-gold dark:text-gray-400"
                  title="Sign out">
            <MdLogout size={16} />
            <span className="hidden sm:inline">Sign out</span>
          </button>
        </div>
      </header>

      {/* Keyed on the path so React remounts this wrapper on every navigation and the
          entrance animation replays. One transition defined here rather than per screen —
          a page that forgot to add it would be the one that felt broken. */}
      <main key={pathname} className="deck-rise mx-auto max-w-6xl px-3 py-4 sm:px-4 sm:py-6">
        <Routes>
          <Route index element={<HomePage />} />
          <Route path="delta/*" element={<DeltaSection />} />
          {/* The policy WORKSPACE — the framework as a tree plus the work on it. Its own
              area, not a part of Project Delta: the plan is a document to read, and nothing
              actionable belongs inside it. Everyone with access may look; the actions on
              each policy are gated per role by the API. */}
          <Route path="policies" element={<PoliciesPage />} />
          {/* A policy's own page sits under the WORKSPACE. It used to live at
              /delta/policies/:reference — inside the presented plan — so opening a policy
              from the workspace dropped you into the plan, and the plan's breadcrumb then
              walked you further into it rather than back where you came from. */}
          <Route path="policies/:reference" element={<PolicyDocumentPage />} />
          <Route path="research" element={<ResearchOSHome />} />
          <Route path="research/quorum"
                 element={<ErrorBoundary name="Quorum"><ResearchPage /></ErrorBoundary>} />
          <Route path="research/chatter"
                 element={<ErrorBoundary name="Chatter"><ChatterPage /></ErrorBoundary>} />
          {/* The search-first master surface: everything ResearchOS holds for one company. */}
          <Route path="research/company/:ticker"
                 element={<ErrorBoundary name="Company"><CompanyProfile /></ErrorBoundary>} />
          <Route path="dashboard" element={<InvestmentDashboard />} />
          <Route path="dashboard/nexus" element={<AssetNexusPage />} />
          <Route path="trading"
                 element={<ErrorBoundary name="Automated Trading"><AutomatedTrading /></ErrorBoundary>} />
          {canWrite && <Route path="register" element={<InventoryPage />} />}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>

      {/* The provisional caveat belongs only where figures appear. On the plan, the policy
          pack and the execution sequence there is not a single computed number on screen,
          and a standing warning about figures there trains the reader to stop seeing it —
          which is exactly what must not happen on the screen that does carry figures. */}
      <footer className="mx-auto max-w-6xl px-3 pb-8 sm:px-4">
        <p className="text-[11px] leading-relaxed text-gray-400 dark:text-gray-600">
          {ILLUSTRATIVE_ROUTES.some((r) => pathname.endsWith(r))
            && 'All figures on this screen are illustrative, not real holdings or returns. '}
          Private and confidential.
        </p>
      </footer>
    </div>
  );
}
